/**
 * Campaign task states as surfaced by the gateway (lower-cased form of
 * `CampaignUserTaskState`). Drives the deliverable task row + submit screen.
 *
 *   performable → validating → completed | rejected
 *   (disabled = not yet actionable, e.g. product not shipped)
 */
export type TaskState = 'performable' | 'validating' | 'completed' | 'rejected' | 'disabled';

export const TASK_STATE = {
  PERFORMABLE: 'performable',
  VALIDATING: 'validating',
  COMPLETED: 'completed',
  REJECTED: 'rejected',
  DISABLED: 'disabled',
} as const;

/** Copy shown while a submitted post is being verified by the backend. */
export const TASK_VALIDATING_MESSAGE = 'Checking post! This can take up to 15 mins.';

/** Copy shown when a deliverable task is not yet actionable. */
export const TASK_DISABLED_MESSAGE = 'Available once tasks above are completed.';

/** Normalize a raw backend task state (upper/lower/null) to a known TaskState. */
export function normalizeTaskState(state?: string | null): TaskState {
  // No state (no CampaignUserTask row yet) → the task hasn't been started.
  if (!state) return TASK_STATE.PERFORMABLE;
  switch (state.toLowerCase()) {
    case 'performable':
      return TASK_STATE.PERFORMABLE;
    case 'validating':
      return TASK_STATE.VALIDATING;
    case 'completed':
      return TASK_STATE.COMPLETED;
    case 'rejected':
      return TASK_STATE.REJECTED;
    case 'disabled':
      return TASK_STATE.DISABLED;
    default:
      // Unknown/new backend state (e.g. a future "under_review") → treat as
      // non-actionable so we never let the creator act on a state we don't handle.
      return TASK_STATE.DISABLED;
  }
}

export const isTaskPerformable = (state?: string | null): boolean =>
  normalizeTaskState(state) === TASK_STATE.PERFORMABLE;
export const isTaskValidating = (state?: string | null): boolean =>
  normalizeTaskState(state) === TASK_STATE.VALIDATING;
export const isTaskCompleted = (state?: string | null): boolean =>
  normalizeTaskState(state) === TASK_STATE.COMPLETED;
export const isTaskRejected = (state?: string | null): boolean =>
  normalizeTaskState(state) === TASK_STATE.REJECTED;
export const isTaskDisabled = (state?: string | null): boolean =>
  normalizeTaskState(state) === TASK_STATE.DISABLED;

/** Whether a task can be (re)submitted from the submit screen. */
export const isTaskSubmittable = (state?: string | null): boolean =>
  isTaskPerformable(state) || isTaskRejected(state);
